import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatDayjsToYmd, parseYmdToDayjs } from '../utils/adminDateUtils';
import { adminDateFieldSx } from './AdminDatePicker.jsx';
import './AdminDateRangeFilter.css';

/**
 * Cross-browser date range (MUI calendar popover — Safari + Chrome).
 * Values are YYYY-MM-DD strings.
 */
export default function AdminDateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className = '',
  startLabel = 'From',
  endLabel = 'To',
  autoOpenEndAfterStart = true,
  presetLabel = null,
  onPresetClick = null
}) {
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const startValue = parseYmdToDayjs(startDate);
  const endValue = parseYmdToDayjs(endDate);

  const applyStart = (nextDayjs, openEnd) => {
    const next = formatDayjsToYmd(nextDayjs);
    if (!next) {
      onStartDateChange('');
      return;
    }
    onStartDateChange(next);
    if (normalizeEndBeforeStart(next, endDate)) {
      onEndDateChange(next);
    }
    if (openEnd && autoOpenEndAfterStart) {
      window.setTimeout(() => setEndPickerOpen(true), 0);
    }
  };

  const applyEnd = (nextDayjs) => {
    const next = formatDayjsToYmd(nextDayjs);
    if (!next) {
      onEndDateChange('');
      return;
    }
    onEndDateChange(next);
    if (normalizeStartAfterEnd(startDate, next)) {
      onStartDateChange(next);
    }
  };

  return (
    <div className={`admin-date-range-filter ${className}`.trim()}>
      <DatePicker
        label={startLabel}
        value={startValue}
        maxDate={endValue || undefined}
        onChange={(v) => applyStart(v, false)}
        onAccept={(v) => applyStart(v, true)}
        format="DD/MM/YYYY"
        slotProps={{
          textField: {
            size: 'small',
            sx: { ...adminDateFieldSx, minWidth: { xs: 140, sm: 158 } }
          },
          htmlInput: { 'aria-label': startLabel },
          field: { clearable: true }
        }}
      />
      <span className="date-separator">to</span>
      <DatePicker
        label={endLabel}
        value={endValue}
        minDate={startValue || undefined}
        open={endPickerOpen}
        onOpen={() => setEndPickerOpen(true)}
        onClose={() => setEndPickerOpen(false)}
        onChange={(v) => applyEnd(v)}
        onAccept={() => setEndPickerOpen(false)}
        format="DD/MM/YYYY"
        slotProps={{
          textField: {
            size: 'small',
            sx: { ...adminDateFieldSx, minWidth: { xs: 140, sm: 158 } }
          },
          htmlInput: { 'aria-label': endLabel },
          field: { clearable: true }
        }}
      />
      {presetLabel && typeof onPresetClick === 'function' ? (
        <button type="button" className="admin-date-range-filter-preset" onClick={onPresetClick}>
          {presetLabel}
        </button>
      ) : null}
    </div>
  );
}

function normalizeEndBeforeStart(start, end) {
  return end && start && end < start;
}

function normalizeStartAfterEnd(start, end) {
  return start && end && start > end;
}
